import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { t } from '@/locales/strings';
import {
  SESSION_STATES,
  initCtx, advance, getDurationMs, getRingMode,
} from '@/utils/playerEngine';
import {
  playConfirmDing,
  playCountdownTick,
  playSqueezeChime,
  playPushChime,
  playReleaseChime,
  playRelaxChime,
  playRestChime,
  vibrate,
} from '@/utils/sound';
import { hapticPattern } from '@/utils/hapticPatterns';
import {
  speak as speakCue,
  silence as silenceVoice,
  isVoiceEnabled,
  setVoiceEnabled,
  prewarm as prewarmVoice,
  prewarmSynth,
} from '@/utils/voiceCoach';
import { resolveVerbKey } from '@/utils/displayVerb';
import Flower from './Flower';
import Pose, { POSE_CITATIONS, POSE_RATIONALE_KEYS } from './Pose';
import PlayerCoachOverlay from '@/components/training/PlayerCoachOverlay';
import { citation, doiUrl } from '@/data/citations';
import { targetLayer, LAYER_PCT } from './targetLayer';
import styles from './RingPlayer.module.css';
import AppContext from '@/contexts/AppContext';
import { cyclePhase, resolveCycleLength } from '@/logic/cycle';
import { sessionRecommendationForPhase, toneColor } from '@/logic/cycleSessionRec';
import { coerceStage } from '@/logic/menopause';

/**
 * RingPlayer — cream-themed, horizontal-layers + flower-on-rail
 * visualization for every exercise (Year 1 + Year 2). Owns the
 * full player lifecycle: state machine, haptics, voice cues, and
 * visualisation.
 *
 * Props:
 *   exercise   - Full exercise object. Supports type: 'hold', 'hold-push',
 *                'pulse', 'alt', 'timed'. movementMode (squeeze/relax/push)
 *                shapes the target layer for hold-type exercises.
 *   lang       - 'en' | 'pt'
 *   onComplete - Called when all sets finish.
 *   onExit     - Called when user taps the back button.
 */
// Short IDLE-screen subtitle derived from the exercise movement + type
// so the per-exercise `sub` field (previously verbose, technique-level
// copy like "CLOSE and SQUEEZE at 50%. Hold 5 seconds.") is bypassed
// in favour of a 2-3 word cue: "Squeeze, relax", "Quick squeezes",
// "Push, relax", "Quick pushes", or "Relax". Pulse is a quick repeat
// of either push or squeeze, not a third movement class. Unmapped
// movements return null so the <p> guard below hides the line rather
// than printing a raw enum.
function deriveIdleSub(exercise, lang) {
  const mv = exercise?.movement;
  const isPulse = exercise?.type === 'pulse';
  if (mv === 'release') return t('rp_idle_sub_relax', lang);
  if (mv === 'squeeze') {
    return t(isPulse ? 'rp_idle_sub_squeeze_quick' : 'rp_idle_sub_squeeze_relax', lang);
  }
  if (mv === 'push') {
    return t(isPulse ? 'rp_idle_sub_push_quick' : 'rp_idle_sub_push_relax', lang);
  }
  return null;
}

// Small offset so the push RELEASE tap lands on the first animation
// frame of the 2400ms settle rather than on the React commit tick
// that precedes the CSS transition's starting value.
const PUSH_RELEASE_HAPTIC_DELAY_MS = 20;

// Breath-shaped haptics. Each phase runs a short pattern rather than
// a one-shot buzz so the phone echoes the breath under the user's palm
// (phone-down training is the default screen-off practice).
//
//   rise   micro-taps of increasing length, mirroring the inhale that
//          precedes the hold (see .dotWrap[data-state="active"] CSS,
//          ~1200 ms rise). Pattern sums to ~640 ms so it settles into
//          the hold, not over it.
//   hold   triple marker. Longer middle tap signals "sustain."
//   lower  taps of decreasing length over a longer spacing, tracking
//          the 1700-2400 ms settle on RELEASE.
//   done   two-beat confirmation with a slight swell so finishing a
//          set reads as earned, not just "next thing."
//
// Every `navigator.vibrate` call cancels the previous pattern, so a
// short phase transition interrupts cleanly. Safe no-op on iOS Safari
// (navigator.vibrate is undefined) and older browsers. Patterns are
// shared with the legacy main.js player via @/utils/hapticPatterns
// so the buzz at each phase boundary is identical across both layers.
const hapticPulse = hapticPattern;

// Request a screen wake lock for the life of the effect. Keeps the
// display on during a 5–10 min exercise so auto-dimming doesn't drop
// the user mid-hold. Gracefully no-ops on browsers without the API
// (older Safari) or when the page loses visibility; re-acquires on
// visibility change.
function useScreenWakeLock(active) {
  useEffect(() => {
    if (!active) return;
    if (typeof navigator === 'undefined' || !navigator.wakeLock) return;

    let sentinel = null;
    let cancelled = false;

    async function acquire() {
      try {
        sentinel = await navigator.wakeLock.request('screen');
        // If the OS releases the lock while we're backgrounded /
        // throttled, null the local ref so the next visibility-
        // regain can re-acquire cleanly. Without this handler the
        // released lock stays stored as "truthy but dead," and
        // the `!sentinel` re-acquire gate never fires.
        sentinel?.addEventListener?.('release', () => {
          if (!cancelled) sentinel = null;
        });
      } catch {
        sentinel = null;
      }
    }

    function onVisibility() {
      if (!cancelled && document.visibilityState === 'visible' && !sentinel) {
        acquire();
      }
    }

    acquire();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      try { sentinel?.release(); } catch {}
      sentinel = null;
    };
  }, [active]);
}

export default function RingPlayer({
  exercise, lang, onComplete, onExit, onSkip,
  exerciseIndex = 0, totalExercises = 1,
  // Vocabulary selection: 'basic' for Year 1 (SQUEEZE / PUSH /
  // RELAX) or 'advanced' for Year 2 (adds LOCK / GRIP / PULL /
  // TILT / TWIST / DRAW / LIFT / PULSE). Caller passes the right
  // vocab based on userProgress.current_year. An exercise can
  // still override per-instance via exercise.verbKey.
  skillVocab = 'basic',
}) {
  const [sessionState, setSessionState] = useState(SESSION_STATES.IDLE);
  const [ctx, setCtx] = useState(initCtx);
  const [timeLeft, setTimeLeft] = useState(0);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  // Mid-session bloom — increments every time a hold completes
  // (ACTIVE → RELEASE/REST/DONE transition). The .bb-rp-bloom
  // overlay re-keys on this value, restarting the keyframe so a
  // fresh bloom plays on each completion. Quiet visual gift.
  const [bloomKey, setBloomKey] = useState(0);
  // Inline "Why this stretch" rationale sheet, surfaced from IDLE for
  // exercises that ship with a pose. Keeps the educational link out
  // of the active session — the sheet only opens by deliberate tap
  // and never auto-closes.
  const [showPoseRationale, setShowPoseRationale] = useState(false);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const advanceRef = useRef(null);
  const prevStateRef = useRef(SESSION_STATES.IDLE);
  // Extra milliseconds added to the current phase via the rest "+30s"
  // button. Reset on every new state entry so it never leaks across
  // phases. Lives in a ref (not state) so the running interval reads
  // the latest value without restarting the timer.
  const phaseExtendMsRef = useRef(0);
  // Base duration of the currently-running phase. Held in a ref so the
  // interval body can read it after a visibility-driven pause without
  // re-deriving from sessionState/ctx (those would be stale closures).
  const currentDurationRef = useRef(0);
  // Timestamp at which the page went hidden mid-phase. null when the
  // page is visible. The visibility handler uses this to compute how
  // long we were hidden and shift startTimeRef forward by that amount,
  // so when the interval restarts the elapsed accounting picks up
  // where it left off.
  const hiddenAtRef = useRef(null);
  // Timestamp at which the user first left the pre-roll (IDLE /
  // INSTRUCTION) and started actually practicing. Reset on Repeat so
  // a second pass through the same exercise gets its own elapsed
  // count. Used by the DONE summary to show total practice time.
  const sessionStartedAtRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  // Attach a 100ms tick that reads from refs (currentDurationRef,
  // phaseExtendMsRef, startTimeRef) so the same interval body works
  // for the initial start, the rest +30s extend, and the
  // visibility-resume path. Pulled out of startTimerForState so we
  // can re-attach mid-phase without recomputing duration.
  const attachInterval = useCallback(() => {
    if (timerRef.current) return;
    if (currentDurationRef.current === 0) return;
    timerRef.current = setInterval(() => {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      const total = (currentDurationRef.current + phaseExtendMsRef.current) / 1000;
      const remaining = Math.max(0, total - elapsed);
      setTimeLeft(remaining);
      if (remaining <= 0) {
        clearTimer();
        setTimeout(() => advanceRef.current?.(), 0);
      }
    }, 100);
  }, [clearTimer]);

  const startTimerForState = useCallback((state, nextCtx) => {
    clearTimer();
    const duration = getDurationMs(state, exercise, nextCtx);
    currentDurationRef.current = duration;
    if (duration === 0) return;
    phaseExtendMsRef.current = 0;
    setTimeLeft(duration / 1000);
    startTimeRef.current = Date.now();
    attachInterval();
  }, [exercise, clearTimer, attachInterval]);

  const advanceState = useCallback(() => {
    const { state: next, ctx: nextCtx } = advance(sessionState, exercise, ctx);
    if (next === SESSION_STATES.DONE) {
      clearTimer();
      setSessionState(SESSION_STATES.DONE);
      return;
    }
    // First step out of the pre-roll: stamp the practice-start moment
    // so the DONE summary can show total time spent. The pre-roll
    // (IDLE → INSTRUCTION) doesn't count; the timer starts the first
    // time we cross into COUNTDOWN or ACTIVE.
    if (
      sessionStartedAtRef.current == null &&
      (next === SESSION_STATES.COUNTDOWN || next === SESSION_STATES.ACTIVE)
    ) {
      sessionStartedAtRef.current = Date.now();
    }
    setCtx(nextCtx);
    setSessionState(next);
    startTimerForState(next, nextCtx);
  }, [sessionState, exercise, ctx, startTimerForState, clearTimer]);

  useEffect(() => { advanceRef.current = advanceState; }, [advanceState]);
  useEffect(() => () => clearTimer(), [clearTimer]);

  // Opt-in autoFlow (Class 1 homework): skip the IDLE / INSTRUCTION
  // pre-roll and go straight into the 3-2-1 countdown, then hand off to
  // the next exercise on DONE without a tap. The caller's own intro
  // screen already did the teaching.
  useEffect(() => {
    if (!exercise?.autoFlow) return;
    sessionStartedAtRef.current = Date.now();
    setSessionState(SESSION_STATES.COUNTDOWN);
    startTimerForState(SESSION_STATES.COUNTDOWN, initCtx());
    // Mount-only: the exercise object is keyed by the caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (exercise?.autoFlow && sessionState === SESSION_STATES.DONE) onComplete?.();
  }, [exercise?.autoFlow, sessionState, onComplete]);

  // Auto-pause on visibility change. If the user backgrounds the app
  // mid-phase (incoming call, switches to messages, locks the screen),
  // the timer would otherwise keep ticking and "auto-complete" reps
  // she never did. On hide we capture the timestamp, clear the
  // interval, and silence any in-flight voice cue. On return we shift
  // startTimeRef forward by the hidden duration so elapsed accounting
  // resumes from the exact ms it was paused at, then re-attach the
  // same interval body. IDLE / INSTRUCTION / DONE never start the
  // timer (currentDurationRef is 0), so this is a no-op there.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    function onVisibility() {
      if (document.visibilityState === 'hidden') {
        if (timerRef.current) {
          hiddenAtRef.current = Date.now();
          clearTimer();
          silenceVoice();
        }
      } else if (document.visibilityState === 'visible' && hiddenAtRef.current != null) {
        const hiddenForMs = Date.now() - hiddenAtRef.current;
        hiddenAtRef.current = null;
        if (currentDurationRef.current > 0 && startTimeRef.current != null) {
          startTimeRef.current += hiddenForMs;
          attachInterval();
        }
      }
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [clearTimer, attachInterval]);

  // Rest controls. The rest screen is the most-stared-at minimal screen
  // in the player, so two small affordances earn their pixels: skip ends
  // the rest immediately and rolls into the next set, extend buys
  // another 30 seconds for women who need a longer reset before the
  // next contraction.
  const skipRest = useCallback(() => {
    if (sessionState !== SESSION_STATES.REST) return;
    advanceState();
  }, [sessionState, advanceState]);
  const extendRest = useCallback(() => {
    if (sessionState !== SESSION_STATES.REST) return;
    phaseExtendMsRef.current += 30_000;
    setTimeLeft((prev) => prev + 30);
  }, [sessionState]);

  // Exit guard. The back arrow used to call onExit immediately, so a
  // stray thumb during a hold could throw away minutes of work. Only
  // states that represent in-progress practice (countdown, active,
  // release, rest) trigger the confirm sheet; from IDLE / INSTRUCTION
  // / DONE there's nothing to lose so the back arrow exits directly.
  const inProgress =
    sessionState === SESSION_STATES.COUNTDOWN ||
    sessionState === SESSION_STATES.ACTIVE ||
    sessionState === SESSION_STATES.RELEASE ||
    sessionState === SESSION_STATES.REST;
  const requestExit = useCallback(() => {
    if (inProgress) {
      setShowExitConfirm(true);
      return;
    }
    if (onExit) onExit();
  }, [inProgress, onExit]);
  const confirmExit = useCallback(() => {
    setShowExitConfirm(false);
    if (onExit) onExit();
  }, [onExit]);
  const cancelExit = useCallback(() => setShowExitConfirm(false), []);

  // Haptic pulse on meaningful phase changes. Skips pre-roll (IDLE,
  // INSTRUCTION) and skips pulse exercises entirely — buzzing on each
  // of 12 pulses per set would be noise, not feedback.
  //
  // Audio cues here are movement-matched (see src/utils/sound.js):
  //   ACTIVE entry from a pre-roll (COUNTDOWN, REST, IDLE)
  //                 → chime that mirrors the movement direction
  //                   (squeeze rises, push falls, relax sustains).
  //                   Intra-pulse rep ACTIVE re-entries are skipped —
  //                   firing a chime per pulse would create the
  //                   "constant ding" the previous build had.
  //   RELEASE      → soft falling tone (skipped on pulse off-phase
  //                   for the same reason).
  //   REST         → low calm chime once on entry. Within-rest ticks
  //                   stay haptic-only (see restHapticRef effect below).
  //   DONE         → existing confirm-ding ack.
  useEffect(() => {
    const prev = prevStateRef.current;
    prevStateRef.current = sessionState;
    if (prev === sessionState) return;
    // Hold-completion bloom — fire when leaving ACTIVE. The
    // bloom overlay re-keys on bloomKey so the keyframe restarts.
    // Pure cosmetic gift; no audio (the existing chime already
    // covers the ACTIVE → REST transition).
    if (prev === SESSION_STATES.ACTIVE) {
      setBloomKey((k) => k + 1);
    }
    // Phase mode (squeeze / push / relax / neutral) for the audio
    // cue — varies per ACTIVE phase on alt exercises and respects the
    // exercise's movementMode for plain holds.
    const phaseMode = getRingMode(sessionState, exercise, ctx);
    // Exercise-level "is this a push hold?" — kept distinct from
    // phaseMode because RELEASE returns mode='relax' regardless, but
    // a push hold's RELEASE still rides the 2.4 s deliberate settle
    // and needs its own haptic timing.
    const isPushExercise = (exercise?.movementMode || exercise?.movement) === 'push';
    const isPulseHere = exercise?.type === 'pulse';
    if (!isPulseHere) {
      if (sessionState === SESSION_STATES.ACTIVE) {
        hapticPulse('rise');
      } else if (sessionState === SESSION_STATES.RELEASE) {
        // Push RELEASE rides a 2400ms deliberate settle. PUSH_RELEASE_HAPTIC_DELAY_MS
        // parks the tap on the first animation frame rather than on
        // the React commit tick, so the buzz aligns with the moment
        // the flower visibly begins its descent. Squeeze RELEASE is
        // fast enough that an immediate tap still reads as aligned.
        if (isPushExercise) setTimeout(() => hapticPulse('lower'), PUSH_RELEASE_HAPTIC_DELAY_MS);
        else hapticPulse('lower');
      } else if (sessionState === SESSION_STATES.DONE) {
        hapticPulse('done');
      }
    } else {
      // Pulse exercises: one-shot set-edge cues only (entry into the
      // first set, end of the last set). The per-pulse metronome tick
      // is driven from a separate effect below so the rhythm is
      // anchored to timeLeft, not to state transitions.
      if (sessionState === SESSION_STATES.DONE) hapticPulse('done');
    }
    // Audio cues. Fire on real set boundaries (entry from COUNTDOWN /
    // REST / IDLE), never on intra-pulse rep transitions.
    const isSetEntry =
      sessionState === SESSION_STATES.ACTIVE &&
      (prev === SESSION_STATES.COUNTDOWN ||
        prev === SESSION_STATES.REST ||
        prev === SESSION_STATES.IDLE ||
        prev === SESSION_STATES.INSTRUCTION);
    if (isSetEntry) {
      if (phaseMode === 'push') playPushChime();
      else if (phaseMode === 'relax') playRelaxChime();
      else playSqueezeChime();
    }
    if (sessionState === SESSION_STATES.RELEASE && !isPulseHere) {
      playReleaseChime();
    }
    if (sessionState === SESSION_STATES.REST) {
      playRestChime();
    }
    if (sessionState === SESSION_STATES.DONE) playConfirmDing();
  }, [sessionState, exercise, ctx]);

  // Pulse haptic metronome. Pulse exercises alternate a short contraction
  // and release at roughly a 1-second cadence; the ring visualisation is
  // the only cue, which makes screen-off training impossible. Firing a
  // short 8 ms tap each time the integer second flips during an ACTIVE
  // pulse set gives the user a felt beat she can follow with her eyes
  // closed. Tracked with a ref so repeated renders during the same tick
  // do not double-fire.
  const pulseTickRef = useRef(null);
  useEffect(() => {
    if (exercise?.type !== 'pulse' || sessionState !== SESSION_STATES.ACTIVE) {
      pulseTickRef.current = null;
      return;
    }
    const shown = Math.ceil(timeLeft);
    if (shown <= 0) return;
    if (pulseTickRef.current === shown) return;
    pulseTickRef.current = shown;
    // Bigger middle beat on the first tick of the set so the entry
    // still reads as a phase transition, then the steady thin tap.
    // Route through the gated vibrate() so the haptic-disabled toggle
    // applies here too — direct navigator.vibrate would bypass it.
    vibrate(6);
  }, [sessionState, exercise?.type, timeLeft]);

  // Display counters — alt pairs vs plain sets. Declared here
  // (before the voice effect below) so the effect's dependency
  // array can reference them without a TDZ reference error.
  const displaySet =
    exercise.type === 'alt'
      ? Math.floor(ctx.setIdx / 2) + 1
      : ctx.setIdx + 1;
  const displayTotalSets =
    exercise.type === 'alt'
      ? Math.ceil((exercise.sets ?? 1) / 2)
      : (exercise.sets ?? 1);
  // Hoisted for the same reason: the voice effect reads `isPulse`
  // in its body and dep array, so it must be in scope by this line.
  const isPulse = exercise?.type === 'pulse';

  // ── Voice coach ──
  // Speaks short cues at phase transitions. The voice IS the
  // metronome — Cátia's "aperta… solta" or Bel's "squeeze… relax."
  // Cancels in-flight utterances so rapid transitions don't pile up.
  // Speaks only on phase entries, not on every render. Pulse plays
  // a single verb per set (not per rep) to avoid chattiness.
  const voicePrevRef = useRef(SESSION_STATES.IDLE);
  useEffect(() => {
    const prev = voicePrevRef.current;
    voicePrevRef.current = sessionState;
    if (prev === sessionState) return;
    const movement = exercise?.movementMode || exercise?.movement;
    const verbKey =
      movement === 'push' ? 'rp_state_push'
      : (movement === 'release' || movement === 'relax') ? 'rp_state_release'
      : 'rp_state_squeeze';

    // Speak the movement verb when entering ACTIVE from a
    // pre-roll / rest state (i.e. the start of a set). Skip the
    // intra-set ACTIVE re-entry on a pulse off→on transition;
    // hearing "squeeze, squeeze, squeeze, squeeze" on a 1s rhythm
    // is exhausting.
    //
    // On the second set onwards (prev === REST), prefix the verb
    // with "Set {n}" so the user hears which set she's on without
    // having to look at the screen. The first set (prev ===
    // COUNTDOWN / IDLE / INSTRUCTION) skips the prefix since
    // "Set one squeeze" would feel heavy at the start.
    if (
      sessionState === SESSION_STATES.ACTIVE &&
      (prev === SESSION_STATES.COUNTDOWN ||
        prev === SESSION_STATES.REST ||
        prev === SESSION_STATES.IDLE ||
        prev === SESSION_STATES.INSTRUCTION)
    ) {
      const verb = t(verbKey, lang);
      if (prev === SESSION_STATES.REST && displaySet > 1 && displayTotalSets > 1) {
        const setPrefix = t('voice_set_prefix', lang).replace('{n}', String(displaySet));
        speakCue(`${setPrefix}. ${verb}`, lang);
      } else {
        speakCue(verb, lang);
      }
    }
    // RELAX cue at the descent of a non-pulse phase. Skip pulse —
    // the off-phase is too brief to narrate.
    if (sessionState === SESSION_STATES.RELEASE && !isPulse) {
      speakCue(t('rp_state_release', lang), lang);
    }
    // REST cue once at entry — a single calm word marks the pause.
    if (sessionState === SESSION_STATES.REST && prev !== SESSION_STATES.REST) {
      speakCue(t('rp_state_rest', lang), lang);
    }
  }, [sessionState, lang, exercise, isPulse, displaySet, displayTotalSets]);

  // Stop any in-flight speech when the player unmounts (user backed
  // out, session ended, etc.) so the cue doesn't trail into the
  // next screen.
  useEffect(() => () => silenceVoice(), []);

  // Pre-fetch + decode the most common voice cues when the player
  // mounts (or the language changes). Without this, the very first
  // phase transition pays a network roundtrip + decode for the MP3
  // — typically 100–300 ms on a warm connection. Fire-and-forget;
  // missing cues fall through to synth at speak() time.
  useEffect(() => { prewarmVoice(lang); }, [lang]);

  // Haptic beats at 30 / 10 / 3 seconds remaining during REST. Matches
  // Apple Fitness / Breathe cadence: two subtle taps earlier in the
  // rest to signal progress, one firmer buzz at 3s to cue the user
  // their body back for the next set. Gated on vibrate availability
  // via hapticPulse's internal guard; a no-op on iOS Safari.
  const restHapticRef = useRef(null);
  useEffect(() => {
    if (sessionState !== SESSION_STATES.REST) {
      restHapticRef.current = null;
      return;
    }
    const shown = Math.ceil(timeLeft);
    if (shown === restHapticRef.current) return;
    restHapticRef.current = shown;
    if (shown === 30 || shown === 10) hapticPulse('rise');
    else if (shown === 3) hapticPulse('hold');
  }, [sessionState, timeLeft]);

  // Tick audio on 3 → 2 → 1 during COUNTDOWN. We track the integer
  // second that's currently showing and fire a tick when it changes
  // (including the very first render of the countdown). No tick for
  // the final zero-crossing — that's the ACTIVE entry, handled above
  // with playConfirmDing.
  const countdownTickRef = useRef(null);
  useEffect(() => {
    if (sessionState !== SESSION_STATES.COUNTDOWN) {
      countdownTickRef.current = null;
      return;
    }
    const shown = Math.ceil(timeLeft);
    if (shown <= 0) return;
    if (countdownTickRef.current !== shown) {
      countdownTickRef.current = shown;
      playCountdownTick();
    }
  }, [sessionState, timeLeft]);

  // Keep the screen awake only while actually running through phases
  // (not on IDLE or INSTRUCTION pre-roll, not on DONE). Auto-releases
  // on unmount or when the state leaves the active set.
  const wakeActive =
    sessionState === SESSION_STATES.COUNTDOWN ||
    sessionState === SESSION_STATES.ACTIVE  ||
    sessionState === SESSION_STATES.RELEASE ||
    sessionState === SESSION_STATES.REST;
  useScreenWakeLock(wakeActive);

  // INSTRUCTION used to auto-skip when a per-exercise localStorage
  // "seen" flag was set, so a returning user never re-read the
  // warnings. The screen now exists deliberately — the warnings are
  // shown immediately before each exercise so the body reads them at
  // the moment they matter — so the auto-skip is gone.

  const mode = getRingMode(sessionState, exercise, ctx);
  const { topPct, stateName, activeLayer, phase, breathe } = targetLayer(sessionState, exercise, ctx, mode);

  // Falls back to movement enum ("SQUEEZE"/"RELAX"/"PUSH") so the pill
  // never renders as "3D Method |" with an empty second slot.
  const exerciseName =
    (lang === 'pt' && exercise.name_pt ? exercise.name_pt : exercise.name) ||
    exercise.movement || '';
  const exerciseSub = deriveIdleSub(exercise, lang);

  // Year 1 (basic) keeps the counter to a bare "{n} / {total}" — no
  // "Set" or "Breath" prefix. The big verb (SQUEEZE / PUSH) on the
  // command card already tells her what to do; a label in front of
  // the count is jargon she has to translate. Year 2 keeps the
  // existing "Set n of N" framing where the named-skill granularity
  // earns it.
  const isBasicVocab = skillVocab === 'basic';
  const formatCounter = (n, total) =>
    isBasicVocab
      ? `${n} / ${total}`
      : t('player_set_label', lang).replace('{n}', n).replace('{total}', total);

  // Session-progress fill percentage. Progress increments by (i+1)/total
  // as the user is actively working through exercise i — the bar
  // visualizes commitment, not completion. Only rendered when > 1
  // exercise in the session.
  const progressPct = totalExercises > 1
    ? Math.round(((exerciseIndex + 1) / totalExercises) * 100)
    : 0;
  const progressBar = totalExercises > 1 ? (
    <div
      className={styles.sessionProgress}
      role="progressbar"
      aria-valuenow={exerciseIndex + 1}
      aria-valuemax={totalExercises}
      aria-label={t('rp_exercise_count', lang).replace('{n}', exerciseIndex + 1).replace('{total}', totalExercises)}
    >
      <div className={styles.sessionProgressFill} style={{ width: `${progressPct}%` }} />
    </div>
  ) : null;

  // ── IDLE ──
  // Brief intro: title, subtitle, stats grid, breath line, Begin
  // button. The HOW TO DO IT / WHAT YOU SHOULD FEEL / AVOID bullets
  // are NOT shown here — they live on the dedicated INSTRUCTION
  // screen that opens after Begin so the user reads the warnings
  // immediately before the exercise, not buried under the launch
  // screen.
  if (sessionState === SESSION_STATES.IDLE) {
    return (
      <div className={styles.ringPlayerRoot}>
        {progressBar}
        <TopBar onExit={requestExit} onSkip={onSkip} eyebrow={t('rp_series_method', lang)} name={exerciseName} lang={lang} />
        <div className={styles.centerStack}>
          <div className={styles.doneTitle}>{exerciseName}</div>
          {exerciseSub && <p className={styles.idleSubtitle}>{exerciseSub}</p>}
          {/* Stretch exercises ship with a pose silhouette. Showing it
              here gives the user a visual reference of the shape her
              body will make before she taps Begin, instead of meeting
              the silhouette for the first time mid-hold. */}
          {exercise.pose && (
            <Pose pose={exercise.pose} label={exerciseName} variant="preview" />
          )}
          {exercise.pose && POSE_RATIONALE_KEYS[exercise.pose] && (
            <button
              type="button"
              className={styles.poseWhyLink}
              onClick={() => setShowPoseRationale(true)}
            >
              {t('rp_pose_why_btn', lang)}
            </button>
          )}
          <IdleStats exercise={exercise} lang={lang} skillVocab={skillVocab} />
          {/* Phase-aware tone hint — gently names the rhythm the body
              is in today (luteal, menstrual, etc.) and the practice
              tone that meets it. Renders nothing if the user isn't
              tracking. The hint is informational, not prescriptive —
              we never swap the session. */}
          <PhaseHint lang={lang} />
          {/* Breath line teaching — one quiet line that explains the
              sage band the user will see inside the canal. Without
              this, the breath line reads as decoration. With it, the
              user knows to ride her breath. */}
          <div className={styles.breathTip}>
            <span className={styles.breathTipDot} aria-hidden="true" />
            <span>{t('rp_breath_tip', lang)}</span>
          </div>
          <button
            type="button"
            className={styles.ctaPrimary}
            onClick={() => {
              prewarmSynth();
              advanceState();
            }}
          >
            <IconPlay stroke="#fff" />
            <span>{t('begin_btn', lang)}</span>
          </button>
        </div>
        {showPoseRationale && exercise.pose && (
          <PoseRationaleSheet
            pose={exercise.pose}
            poseLabel={exerciseName}
            lang={lang}
            onClose={() => setShowPoseRationale(false)}
          />
        )}
      </div>
    );
  }

  // ── INSTRUCTION ──
  // Shown after Begin, before the countdown / first squeeze. The
  // warnings (HOW TO DO IT, WHAT YOU SHOULD FEEL, AVOID) live here
  // so the user reads them at the moment they matter — right before
  // the body actually does the thing. The title repeats so she knows
  // what she's reading instructions for.
  if (sessionState === SESSION_STATES.INSTRUCTION) {
    return (
      <div className={styles.ringPlayerRoot}>
        {progressBar}
        <TopBar onExit={requestExit} onSkip={onSkip} eyebrow={t('rp_series_method', lang)} name={exerciseName} lang={lang} />
        <div className={styles.centerStack}>
          <div className={styles.doneTitle}>{exerciseName}</div>
          {exercise.pose && (
            <Pose pose={exercise.pose} label={exerciseName} variant="preview" />
          )}
          <InstructionBullets exercise={exercise} lang={lang} />
          <button type="button" className={styles.ctaPrimary} onClick={() => advanceState()}>
            <IconPlay stroke="#fff" />
            <span>{t('rp_start_now_btn', lang)}</span>
          </button>
        </div>
      </div>
    );
  }

  // NOTE: COUNTDOWN + REST used to early-return their own standalone
  // layouts here, which unmounted the stage completely. That made
  // the flower mount fresh *at its target position* when ACTIVE
  // started, so the very first squeeze had no rising animation —
  // the user reported the flower "didn't move with the squeeze."
  //
  // Fix: render the main layout unconditionally, then overlay the
  // fullscreen COUNTDOWN / REST UI on top (opaque background at
  // z-index 10). The stage stays mounted, the flower stays at rest
  // position (topPct=100 from targetLayer for both states), and
  // the transition from rest → target fires the first time ACTIVE
  // starts — producing the squeeze rise the user expects.

  // ── DONE ──
  if (sessionState === SESSION_STATES.DONE) {
    // Per-exercise summary stats. Sets done is the configured total
    // (DONE is only reachable after the engine confirms every set
    // completed). Time is real elapsed since first leaving pre-roll,
    // so a paused-via-visibility session reports the practice time
    // accurately, not the wall-clock time.
    const setsDone = exercise?.type === 'alt'
      ? Math.ceil((exercise?.sets ?? 1) / 2)
      : (exercise?.sets ?? 1);
    const elapsedMs = sessionStartedAtRef.current
      ? Math.max(0, Date.now() - sessionStartedAtRef.current)
      : 0;
    const totalSec = Math.round(elapsedMs / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    const timeLabel = m > 0
      ? `${m}:${String(s).padStart(2, '0')}`
      : `${s}s`;
    const setsKey = setsDone === 1 ? 'rp_done_stat_sets_one' : 'rp_done_stat_sets_many';
    const setsLabel = t(setsKey, lang).replace('{n}', String(setsDone));
    return (
      <div className={styles.ringPlayerRoot}>
        {progressBar}
        <TopBar onExit={requestExit} onSkip={onSkip} eyebrow={t('rp_series_method', lang)} name={exerciseName} lang={lang} />
        <div className={styles.centerStack}>
          <div className={styles.doneTitle}>
            <IconCheck stroke="var(--rp-sage-500)" />
            <span>{t('complete_title', lang)}</span>
          </div>
          {totalSec > 0 && (
            <div className={styles.doneStats} aria-label={t('rp_done_stats_aria', lang)}>
              <span className={styles.doneStatsItem}>{setsLabel}</span>
              <span className={styles.doneStatsDot} aria-hidden="true" />
              <span className={styles.doneStatsItem}>{timeLabel}</span>
            </div>
          )}
          <div className={styles.doneRow}>
            <button
              type="button" className={styles.ctaSecondary}
              onClick={() => {
                sessionStartedAtRef.current = null;
                setSessionState(SESSION_STATES.IDLE);
                setCtx(initCtx());
              }}
            >
              <IconRepeat stroke="currentColor" />
              <span>{t('complete_repeat', lang)}</span>
            </button>
            <button type="button" className={styles.ctaPrimary} onClick={onComplete}>
              <IconCheck stroke="#fff" />
              <span>{t('complete_done', lang)}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── ACTIVE / RELEASE / COUNTDOWN / REST ──

  // Sub-phase timing. The big word on the command card is a verb the
  // user acts on (SQUEEZE / PUSH / RELEASE), not a label for the whole
  // phase. It shows only while the user is moving — matched to the
  // flower's visible travel — and then hands off to HOLD (or empty) for
  // the sustained / rest portion of the phase.
  //   HOLD_MOVE_MS     — flower rise duration for squeeze / push holds
  //                      (matches .dotWrap[data-state="hold"] CSS).
  //   RELEASE_MOVE_MS  — flower descent on RELEASE
  //                      (matches .dotWrap[data-state="release"] CSS).
  const HOLD_MOVE_MS = 1200;
  const RELEASE_MOVE_MS = 1700;

  const isPushMode = mode === 'push';

  // Elapsed ms inside the current phase — drives verb → HOLD handoff
  // for hold-type exercises. timeLeft updates every 100ms so the crossover
  // happens within ~100ms of the exact moveMs mark.
  const phaseDurationMs =
    sessionState === SESSION_STATES.ACTIVE || sessionState === SESSION_STATES.RELEASE
      ? getDurationMs(sessionState, exercise, ctx)
      : 0;
  const elapsedMs = phaseDurationMs > 0
    ? Math.max(0, phaseDurationMs - timeLeft * 1000)
    : 0;

  // Hold sub-phase: true while the user is sustaining the contraction
  // (big word is HOLD). False while still moving (big word is verb) or
  // in a non-hold exercise (pulse / rock-release alt / idle relax).
  //
  // An exercise only earns the HOLD label if it's truly isometric —
  // a literal "sustain the contraction" beat. Under 5 seconds the
  // squeeze IS the exercise (quick squeeze-release, no held beat),
  // so the verb stays the whole time. 5+ seconds crosses the
  // endurance-hold threshold: once the contraction runs long
  // enough that sustaining it becomes the practice, the label
  // becomes HOLD after the initial rise animation. Exercises can
  // override via an explicit `isometric: true | false` flag on the
  // exercise object for special cases.
  const isRockRelease =
    exercise?.type === 'alt'
    && stateName === 'release'
    && !isPushMode;
  const isIsometric =
    exercise?.isometric != null
      ? !!exercise.isometric
      : (exercise?.hold ?? 0) >= 5;
  const isHoldPhase =
    sessionState === SESSION_STATES.ACTIVE
    && !isPulse
    && !isRockRelease
    && isIsometric
    && elapsedMs >= HOLD_MOVE_MS;

  // Main command shown in the bottom card. Vocabulary is intentionally
  // restricted: COUNTDOWN digit, then SQUEEZE / PUSH / RELEASE / HOLD /
  // REST. Empty string means no word — deliberate rest for the eyes.
  let command = '';
  if (sessionState === SESSION_STATES.COUNTDOWN) {
    command = String(Math.max(1, Math.ceil(timeLeft)));
  } else if (sessionState === SESSION_STATES.REST) {
    command = t('rp_state_rest', lang);
  } else if (sessionState === SESSION_STATES.RELEASE) {
    // Pulse RELEASE is the brief off-phase — leave it empty so the
    // verb flash on each on-phase feels like a flash, not a ping-pong.
    // Push exercises keep the RELEASE label visible through the full
    // descent (the slow elevator ride) so the user has a persistent
    // anchor during the 6s relax. Other hold types fade the label
    // after the initial descent animation (1.7s) so the remaining
    // relax reads as a pause for the eyes.
    if (isPulse) command = '';
    else if (isPushMode) command = t('rp_state_release', lang);
    else if (elapsedMs < RELEASE_MOVE_MS) command = t('rp_state_release', lang);
    else command = '';
  } else if (sessionState === SESSION_STATES.ACTIVE) {
    // Verb for the movement itself. Order matters: isPushMode wins over
    // stateName === 'release' because push holds have stateName='release'
    // (the flower descends on a bear-down), but the action is PUSH.
    //
    // The verb stays through the whole ACTIVE phase — rise and sustain.
    // For an isometric (5s+) hold, a secondary "hold" chip renders
    // beneath the verb while the sustain is active, so the word
    // stays SQUEEZE (or PUSH / RELEASE) and HOLD is a second beat
    // rather than a replacement. Matches the user's "movement + hold"
    // framing of "movement + sustain."
    // Vocabulary resolver — returns 'rp_state_squeeze' by default or
    // one of the Year 2 named skills (LOCK / GRIP / PULL / TILT /
    // DRAW / LIFT / PULSE) when skillVocab === 'advanced'. An
    // explicit exercise.verbKey always wins.
    const verbKey = resolveVerbKey({
      exercise, ctx, sessionState, stateName, isPushMode, skillVocab,
    });
    command = t(verbKey, lang);
  }

  // State attr for CSS colour coding of the command card.
  const commandState =
    sessionState === SESSION_STATES.COUNTDOWN ? 'countdown'
    : sessionState === SESSION_STATES.REST ? 'rest'
    : isHoldPhase ? 'hold'
    : sessionState === SESSION_STATES.RELEASE ? 'release'
    : sessionState === SESSION_STATES.ACTIVE && isPushMode ? 'push'
    : sessionState === SESSION_STATES.ACTIVE && stateName === 'release' ? 'release'
    : sessionState === SESSION_STATES.ACTIVE ? 'squeeze'
    : '';

  // (instructionText derivation removed — the bottom instruction
  // card was retired in favour of teaching exclusively on IDLE.)

  // (showTopHold removed — the corner HOLD chip was retired when
  // the verb stays visible through the sustain instead.)

  // Show integer timer under the command during timed phases that
  // have a meaningful remaining duration. COUNTDOWN's digit IS the
  // command itself, so no secondary timer. Pulses don't need a timer
  // (each on/off is 1s) — the flash rhythm carries the tempo.
  const showTimer =
    !isPulse && (
      sessionState === SESSION_STATES.ACTIVE ||
      sessionState === SESSION_STATES.RELEASE ||
      sessionState === SESSION_STATES.REST
    );

  // Key for the command element so the pop-in animation retriggers at
  // the right beats:
  //   pulse       — key per rep so each on-phase flashes fresh
  //   hold / rest — key per transition verb (SQUEEZE → HOLD → RELEASE
  //                 all remount, so the pop animation fires on each
  //                 handoff). Set index is part of the key so the same
  //                 verb on a new set still triggers the flash.
  const commandKey = isPulse
    ? `${sessionState}-${ctx.setIdx}-${ctx.repIdx}`
    : `${sessionState}-${ctx.setIdx}-${command}`;

  // Focus Mode is on during ACTIVE and RELEASE of hold-type exercises —
  // the screen strips to just the verb, count, timer, and canal so the
  // woman can work without reading a menu.
  //
  // Off for:
  //   • pulse — rapid 1-s on/off rhythm is carried by the flower's
  //     motion, not a sustained verb, so the full stage stays.
  //   • alt   — the two phases (A/B, squeeze/release or front/back)
  //     are both meaningful and benefit from the Roman-numeral cues;
  //     swapping to Focus Mode only on phase A (as the previous
  //     `isRockRelease` gate did) flickered the UI mid-exercise.
  const focusMode =
    (sessionState === SESSION_STATES.ACTIVE || sessionState === SESSION_STATES.RELEASE)
    // Pulses that opt in with showReps (Class 1 homework) use Focus Mode
    // too, so both homework exercises share one clean layout.
    && (!isPulse || exercise?.showReps)
    && exercise?.type !== 'alt';

  return (
    <div
      className={styles.ringPlayerRoot}
      data-phase={commandState || undefined}
      data-focus={focusMode ? 'on' : undefined}
      data-steady={exercise.steadyStage ? 'on' : undefined}
    >
      {progressBar}
      <TopBar onExit={requestExit} onSkip={onSkip} eyebrow={t('rp_series_method', lang)} name={exerciseName} lang={lang} />

      {/* Mid-session bloom — a brief radial glow that pulses
          across the player each time a hold completes (ACTIVE
          → REST / RELEASE / DONE). Re-keying on bloomKey
          restarts the keyframe. Pointer-transparent so it never
          intercepts taps; mounted as a sibling of the rings so
          its filter doesn't repaint the whole tree. */}
      <span
        key={bloomKey}
        aria-hidden="true"
        className="bb-rp-bloom"
        style={{ display: bloomKey > 0 ? 'block' : 'none' }}
      />

      {/* First-session coaching layer. Renders only on a user's
          very first time playing through the player; gated by
          intro_state.player_coached. Three captions cycle in
          three on-screen positions to point at the flower, the
          breath line, and the voice toggle. */}
      {!exercise.autoFlow && <PlayerCoachOverlay lang={lang} />}

      {/* Command / count card. During Focus Mode the card goes
          transparent and the set counter is promoted to its own small
          badge above the verb (rather than an inline parenthetical),
          so the three anchors — count, verb, timer — stack cleanly
          centered. */}
      <div className={styles.cue} data-phase={commandState || undefined}>
        {focusMode && displayTotalSets > 1 && (
          <div
            key={`set-${ctx.setIdx}`}
            className={styles.setBadge}
            aria-label={t('player_set_label', lang).replace('{n}', displaySet).replace('{total}', displayTotalSets)}
          >
            {exercise.showReps && (exercise.reps ?? 1) > 1
              // Opt-in (Class 1 homework): count squeezes / pulses inside
              // the set so the badge moves on every rep, not once per set.
              ? t('player_rep_badge', lang)
                  .replace('{r}', Math.min(ctx.repIdx + 1, exercise.reps))
                  .replace('{reps}', exercise.reps)
                  .replace('{n}', displaySet)
                  .replace('{total}', displayTotalSets)
              : formatCounter(displaySet, displayTotalSets)}
          </div>
        )}
        {command ? (
          <div
            key={commandKey}
            className={styles.commandTitle}
            data-s={commandState}
            aria-live={isPulse ? 'off' : 'polite'}
            aria-atomic="true"
          >
            {command}
            {!focusMode && !isBasicVocab && sessionState === SESSION_STATES.ACTIVE && !isPulse && displayTotalSets > 1 && (
              <span className={styles.commandSetCount} aria-hidden="true">
                {' '}({displaySet}/{displayTotalSets})
              </span>
            )}
          </div>
        ) : (
          /* Placeholder keeps the card height stable during empty
             beats (pulse off-phase, post-release rest) so the card
             doesn't pop up and down between flashes. */
          <div className={styles.commandPlaceholder} aria-hidden="true" />
        )}
        {/* Timer is reserved for non-Focus-Mode phases (alt phases,
            rare edge cases). During Focus Mode the flower's position
            and the breath line inside the canal carry the rhythm —
            no visible countdown competes with the body. */}
        {showTimer && !focusMode && (
          <div className={styles.cueTimer} data-on="true" aria-label={`${Math.ceil(timeLeft)} ${t('rp_sec_unit', lang)}`}>
            <span aria-hidden="true">{Math.ceil(timeLeft)}</span>
            <span className="rp-unit" aria-hidden="true">{t('rp_sec_unit', lang)}</span>
          </div>
        )}
      </div>

      {/* Stage */}
      <div className={styles.stage}>
        <div className={styles.stageInner}>
          {exercise.pose ? (
            <Pose pose={exercise.pose} label={exerciseName} />
          ) : (
          <>
          {/* Canal silhouette — decorative anatomy cue behind the rail.
              Three subtle rose bands inside a rounded teardrop outline
              so the flower's rise/fall reads as movement *through* the
              pelvic floor, not against a neutral background. Static
              (no active-zone tint) — the Roman numerals on the left
              already brighten for the active layer. */}
          <div className={styles.canal} aria-hidden="true">
            <svg
              className={styles.canalSvg}
              viewBox="0 0 100 200"
              preserveAspectRatio="none"
            >
              <defs>
                <clipPath id="rpCanalClip" clipPathUnits="userSpaceOnUse">
                  <path d="M 38 6 Q 50 0 62 6 C 68 14 72 70 72 120 C 72 170 66 192 50 196 C 34 192 28 170 28 120 C 28 70 32 14 38 6 Z" />
                </clipPath>
              </defs>
              <g clipPath="url(#rpCanalClip)">
                <rect x="0" y="0"   width="100" height="68" className={styles.canalBandTop} />
                <rect x="0" y="68"  width="100" height="64" className={styles.canalBandMid} />
                <rect x="0" y="132" width="100" height="68" className={styles.canalBandBot} />
              </g>
              <path
                d="M 38 6 Q 50 0 62 6 C 68 14 72 70 72 120 C 72 170 66 192 50 196 C 34 192 28 170 28 120 C 28 70 32 14 38 6 Z"
                className={styles.canalOutline}
              />
              {/* Breath indicator — horizontal sage band that sits
                  at the same vertical position as the flower, so the
                  two elements rise and fall as one body. viewBox is
                  0..200 in y; topPct is 0..100 from the stage top,
                  so y = topPct * 2; subtract 1.5 to centre the
                  3-unit band on the flower. Clipped to the canal so
                  it reads as breath *inside the body*. The CSS
                  transition on y keeps the line's movement locked to
                  the flower's own rise / fall. */}
              <g clipPath="url(#rpCanalClip)" className={styles.canalBreathGroup}>
                <rect
                  x="0"
                  y={topPct * 2 - 1.5}
                  width="100"
                  height="3"
                  className={styles.canalBreathLine}
                />
              </g>
            </svg>
          </div>
          {/* Roman-numeral left column. Hidden on Year 1 basic — the
              3-level lift is a *next* lesson, not a Foundation one,
              and showing labels for it now makes the screen read as
              clinical rather than "do this one squeeze." Year 2 (or
              wherever skillVocab='advanced' opts in) keeps the column. */}
          {!isBasicVocab && (
            <div className={styles.levelCol}>
              <div
                className={styles.levelRow}
                data-active={activeLayer === 3 ? 'true' : 'false'}
                style={{ top: `${LAYER_PCT[3]}%` }}
              >
                <div className="rp-roman">III</div>
                <div className="rp-desc">{t('rp_level_deep', lang)}</div>
              </div>
              <div
                className={styles.levelRow}
                data-active={activeLayer === 2 ? 'true' : 'false'}
                style={{ top: `${LAYER_PCT[2]}%` }}
              >
                <div className="rp-roman">II</div>
                <div className="rp-desc">{t('rp_level_central', lang)}</div>
              </div>
              <div
                className={styles.levelRow}
                data-active={activeLayer === 1 ? 'true' : 'false'}
                style={{ top: `${LAYER_PCT[1]}%` }}
              >
                <div className="rp-roman">I</div>
                <div className="rp-desc">{t('rp_level_entry', lang)}</div>
              </div>
            </div>
          )}

          {/* Dashed layer lines. Same gating: hidden on Year 1 basic
              so the canal reads as a single soft container, not a
              graduated meter the user feels she's "supposed" to be
              hitting marks on. */}
          {!isBasicVocab && (
            <div className={styles.layers}>
              <div
                className={styles.layer}
                data-active={activeLayer >= 3 ? 'true' : 'false'}
                style={{ top: `${LAYER_PCT[3]}%` }}
              />
              <div
                className={styles.layer}
                data-active={activeLayer >= 2 ? 'true' : 'false'}
                style={{ top: `${LAYER_PCT[2]}%` }}
              />
              <div
                className={styles.layer}
                data-active={activeLayer >= 1 ? 'true' : 'false'}
                style={{ top: `${LAYER_PCT[1]}%` }}
              />
            </div>
          )}

          {/* Edge labels */}
          <div className={`${styles.edgeLbl} ${styles.edgeLblTop}`}>{t('rp_edge_rise', lang)}</div>
          <div className={`${styles.edgeLbl} ${styles.edgeLblBottom}`}>{t('rp_edge_soften', lang)}</div>

          {/* Rail + flower */}
          <div className={styles.railZone}>
            <div className={styles.railLine} />
            <div
              className={styles.dotWrap}
              data-state={stateName}
              data-type={exercise.type || 'hold'}
              data-mode={exercise.movementMode || exercise.movement || undefined}
              data-session-state={sessionState}
              data-phase={phase || undefined}
              data-breathe={breathe ? 'true' : undefined}
              style={{ top: `${topPct}%` }}
            >
              <div className={styles.dotHalo} />
              <Flower />
            </div>
          </div>
          </>
          )}
        </div>
      </div>

      {/* Footer: set counter */}
      <div className={styles.footer}>
        <div className={styles.setCounter}>
          {formatCounter(displaySet, displayTotalSets)}
          {(exercise.type === 'pulse' || exercise.repeatHolds) && (exercise.reps ?? 1) > 1 && (
            <span> · {ctx.repIdx + 1}/{exercise.reps}</span>
          )}
        </div>
      </div>

      {/* COUNTDOWN overlay — opaque fullscreen digit that sits on top
          of the mounted stage. Stage behind stays at rest (topPct=100)
          so the flower's transition from rest → target fires the
          first time ACTIVE starts. */}
      {sessionState === SESSION_STATES.COUNTDOWN && (() => {
        const digit = String(Math.max(1, Math.ceil(timeLeft)));
        return (
          <div className={styles.fullscreenCountdown}>
            <div
              key={digit}
              className={styles.fullscreenCountdownDigit}
              aria-live="assertive"
              aria-atomic="true"
              aria-label={`${digit} ${t('rp_sec_unit', lang)}`}
            >
              {digit}
            </div>
          </div>
        );
      })()}

      {/* REST overlay — same ceremonial digit as COUNTDOWN but
          softened in tone and labelled with the upcoming set. Own
          exit button stays reachable during the longer rest. */}
      {sessionState === SESSION_STATES.REST && (() => {
        const digit = String(Math.max(0, Math.ceil(timeLeft)));
        const upcomingSet =
          exercise.type === 'alt'
            ? Math.floor(ctx.setIdx / 2) + 1
            : ctx.setIdx + 1;
        return (
          <div className={styles.restFullscreen}>
            <button
              type="button" className={styles.restExitBtn}
              onClick={requestExit}
              aria-label={t('player_exit', lang)}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M14.5 18.5 8 12l6.5-6.5" />
              </svg>
            </button>
            <div className={styles.restLabel}>
              {exercise.showReps
                ? t('player_set_label', lang).replace('{n}', upcomingSet).replace('{total}', displayTotalSets)
                : isBasicVocab
                ? formatCounter(upcomingSet, displayTotalSets)
                : `${t('rp_state_rest', lang)} · ${upcomingSet}/${displayTotalSets}`}
            </div>
            <div
              key={digit}
              className={styles.fullscreenCountdownDigit}
              aria-live="assertive"
              aria-atomic="true"
              aria-label={`${digit} ${t('rp_sec_unit', lang)}`}
            >
              {digit}
            </div>
            <div className={styles.restHint}>
              {t('rp_rest_subtitle', lang)}
            </div>
            <div className={styles.restActions}>
              <button
                type="button" className={styles.restActionBtn}
                onClick={extendRest}
                aria-label={t('rp_rest_extend_btn', lang)}
              >
                {t('rp_rest_extend_btn', lang)}
              </button>
              <button
                type="button" className={`${styles.restActionBtn} ${styles.restActionBtnPrimary}`}
                onClick={skipRest}
                aria-label={t('rp_rest_skip_btn', lang)}
              >
                {t('rp_rest_skip_btn', lang)}
              </button>
            </div>
          </div>
        );
      })()}

      {/* Exit confirmation. Only the in-progress states (countdown
          through rest) ever reach here — IDLE / INSTRUCTION / DONE
          short-circuit requestExit and call onExit directly. */}
      {showExitConfirm && (
        <div
          className={styles.exitConfirmOverlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="rp-exit-confirm-title"
          onClick={(e) => { if (e.target === e.currentTarget) cancelExit(); }}
        >
          <div className={styles.exitConfirmSheet}>
            <h2 id="rp-exit-confirm-title" className={styles.exitConfirmTitle}>
              {t('rp_exit_confirm_title', lang)}
            </h2>
            <p className={styles.exitConfirmBody}>
              {t(exercise.autoFlow ? 'rp_exit_confirm_body_restart' : 'rp_exit_confirm_body', lang)}
            </p>
            <div className={styles.exitConfirmActions}>
              <button
                type="button"
                className={styles.exitConfirmBtn}
                onClick={cancelExit}
                autoFocus
              >
                {t('rp_exit_confirm_stay', lang)}
              </button>
              <button
                type="button"
                className={`${styles.exitConfirmBtn} ${styles.exitConfirmBtnDanger}`}
                onClick={confirmExit}
              >
                {t('rp_exit_confirm_leave', lang)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── TopBar: back button + series pill ──
function TopBar({ onExit, onSkip, eyebrow, name, lang }) {
  // Voice coach toggle. Local state mirrors the persisted flag so the
  // icon flips immediately on tap; the source of truth lives in
  // localStorage via voiceCoach.setVoiceEnabled.
  const [voiceOn, setVoiceOn] = useState(() => {
    try { return isVoiceEnabled(); } catch { return true; }
  });
  const toggleVoice = useCallback(() => {
    const next = !voiceOn;
    setVoiceOn(next);
    setVoiceEnabled(next);
  }, [voiceOn]);

  return (
    <div className={styles.topBar}>
      <button type="button" className={styles.iconBtn} onClick={onExit} aria-label={t('player_exit', lang)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M14.5 18.5 8 12l6.5-6.5" />
        </svg>
      </button>
      <div className={styles.series}>
        <div className={styles.seriesPill} title={name || undefined}>
          <span className="rp-eyebrow">{eyebrow}</span>
          {name && <span className="rp-divider" />}
          {name && <span className="rp-name">{name}</span>}
        </div>
      </div>
      {/* Right-side icon cluster. Wraps voice + (optional) skip in a
          flex row so both fit inside the right grid column without
          spilling onto a new row. .topBar is a 3-column grid
          (40px 1fr 40px); without this wrapper the skip button is
          placed into the implicit second grid row directly under the
          back button. */}
      <div style={{ display: 'flex', gap: 8, justifySelf: 'end' }}>
        <button
          type="button" className={styles.iconBtn}
          onClick={toggleVoice}
          aria-label={voiceOn ? t('rp_voice_mute', lang) : t('rp_voice_unmute', lang)}
          aria-pressed={voiceOn}
        >
          {voiceOn ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M11 5 6 9H3v6h3l5 4V5z" />
              <path d="M16 9a4 4 0 0 1 0 6" />
              <path d="M19 6a8 8 0 0 1 0 12" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M11 5 6 9H3v6h3l5 4V5z" />
              <path d="M22 9l-6 6M16 9l6 6" />
            </svg>
          )}
        </button>
        {/* Skip-to-next-exercise button. Distinct from the back/exit
            button on the left: skip advances within the session
            (counts the exercise as done and moves on), exit leaves
            the session entirely. Hidden when the parent doesn't pass
            onSkip (standalone player previews, the onboarding
            tryout). */}
        {onSkip && (
          <button
            type="button" className={styles.iconBtn}
            onClick={onSkip}
            aria-label={t('player_skip', lang)}
            title={t('player_skip', lang)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polygon points="5 4 15 12 5 20" fill="currentColor" stroke="none" />
              <line x1="19" y1="5" x2="19" y2="19" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

// ── IdleStats: compact pill row showing exercise structure ──
// Gives the IDLE screen visual rhythm when the exercise has no
// inst/feel/avoid copy (e.g. the onboarding Squeeze tryout), and
// previews the session shape at a glance even when bullets are
// present. Renders nothing if no stats are available.
function IdleStats({ exercise, lang, skillVocab = 'advanced' }) {
  const chips = [];
  // Year 1 (basic) reduces the chip row to one short pairing: the
  // movement verb + the hold duration. Sets, reps, rest — none of
  // it appears here. Foundation is about mastering one verb
  // (SQUEEZE or PUSH); the count is in the footer, not the chip
  // row, and "rest" between sets isn't a thing the user has to
  // think about at this stage.
  if (skillVocab === 'basic') {
    const movement = exercise?.movementMode || exercise?.movement;
    const verbKey =
      movement === 'push' ? 'rp_state_push'
      : (movement === 'release' || movement === 'relax') ? 'rp_state_release'
      : 'rp_state_squeeze';
    const verb = t(verbKey, lang);
    if (exercise?.hold) {
      chips.push(`${verb} · ${exercise.hold}s`);
    } else {
      chips.push(verb);
    }
  } else {
    if (exercise?.sets && exercise?.reps) {
      chips.push(t('rp_idle_stat_sets_reps', lang).replace('{sets}', exercise.sets).replace('{reps}', exercise.reps));
    }
    if (exercise?.hold) {
      chips.push(t('rp_idle_stat_hold', lang).replace('{sec}', exercise.hold));
    }
    if (exercise?.rest) {
      chips.push(t('rp_idle_stat_rest', lang).replace('{sec}', exercise.rest));
    }
  }
  // Set-progress preview — a hollow-circle row showing the upcoming
  // sets so the user has a visual handle on "how much of this will I
  // be doing?" before tapping Begin. Aligns with the WeekDots
  // language on the home screen (filled = done, hollow = not-yet).
  const totalSets = exercise?.type === 'alt'
    ? Math.ceil((exercise?.sets ?? 1) / 2)
    : (exercise?.sets ?? 1);
  const showSetDots = totalSets > 1 && totalSets <= 12;
  if (!chips.length && !showSetDots) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
      {chips.length > 0 && (
        <div className={styles.idleStats}>
          {chips.map((label, i) => (
            <React.Fragment key={label}>
              {i > 0 && <span className={styles.idleStatsDot} aria-hidden="true" />}
              <span className={styles.idleStatsItem}>{label}</span>
            </React.Fragment>
          ))}
        </div>
      )}
      {showSetDots && (
        <div
          role="group"
          aria-label={t('rp_idle_sets_preview_aria', lang).replace('{n}', String(totalSets))}
          style={{ display: 'flex', gap: 6 }}
        >
          {Array.from({ length: totalSets }).map((_, i) => (
            <span
              key={i}
              aria-hidden="true"
              style={{
                width: 7, height: 7,
                borderRadius: '50%',
                border: '1.3px solid color-mix(in srgb, var(--rp-rose-300) 55%, transparent)',
                background: 'transparent',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── PhaseHint: one-line cycle-phase tone note on IDLE ──
// Bridges cycle tracking and the practice: when the user has logged a
// last-period-start date (or is in menopause mode), we surface a quiet
// chip on the launch screen that names the body's current rhythm and
// the tone that meets it. Reads from useApp directly to keep
// RingPlayer's prop surface small. Renders nothing for users who
// aren't tracking, so the player is unchanged when there's no signal
// to share.
function PhaseHint({ lang }) {
  // useContext directly rather than useApp(): characterization tests
  // mount RingPlayer without the AppProvider, and the player itself
  // should never crash when the context is absent — the hint is
  // purely additive.
  const ctx = useContext(AppContext);
  const userProgress = ctx?.userProgress;
  const stage = coerceStage(userProgress?.menopause_stage);
  const cycleInfo = cyclePhase(
    userProgress?.cycle_last_period_start,
    new Date(),
    resolveCycleLength(userProgress, []),
  );
  const rec = sessionRecommendationForPhase(cycleInfo?.phase, stage);
  if (!rec) return null;
  const tint = toneColor(rec.tone);
  return (
    <div
      role="note"
      aria-label={t('rp_phase_hint_aria', lang)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 14px',
        borderRadius: 999,
        background: `color-mix(in srgb, ${tint} 10%, transparent)`,
        border: `1px solid color-mix(in srgb, ${tint} 28%, transparent)`,
        font: '600 11px/1 var(--rp-font-body)',
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        color: tint,
        maxWidth: 320,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 6, height: 6, borderRadius: '50%', background: tint,
        }}
      />
      <span>{t(rec.headlineKey, lang)}</span>
    </div>
  );
}

// ── InstructionBullets: how-to / feel / avoid cards ──
// Shared between IDLE (always rendered, so the user lands on a screen
// that already tells them what to do) and INSTRUCTION (kept as a
// fallback for any flow that bypasses IDLE).
function InstructionBullets({ exercise, lang }) {
  const instText = lang === 'pt' && exercise.inst_pt ? exercise.inst_pt : (exercise.inst || '');
  const feelText = lang === 'pt' && exercise.feel_pt ? exercise.feel_pt : (exercise.feel || '');
  const avoidText = lang === 'pt' && exercise.avoid_pt ? exercise.avoid_pt : (exercise.avoid || '');
  const instLines = instText.split(/\n+/).map(s => s.trim()).filter(Boolean);
  // feel/avoid are single-paragraph fields in year-1 data; split on
  // sentence boundaries so multi-sentence strings render as separate
  // bullet cards rather than one dense paragraph.
  const splitSentences = s => s.split(/(?<=[.!?])\s+/).map(t => t.trim()).filter(Boolean);
  const feelLines = splitSentences(feelText);
  const avoidLines = splitSentences(avoidText);
  if (!instLines.length && !feelLines.length && !avoidLines.length) return null;
  return (
    <>
      {instLines.length > 0 && (
        <div className={styles.instSection}>
          <div className={styles.instSectionLabel}>{t('rp_inst_howto', lang)}</div>
          <div className={styles.instList}>
            {instLines.map((line, i) => (
              <div key={i} className={styles.instCard}>
                <span className={styles.instIconWrap}><IconBullet /></span>
                <span className={styles.instLine}>{line}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {feelLines.length > 0 && (
        <div className={styles.instSection}>
          <div className={styles.instSectionLabel}>{t('rp_inst_feel', lang)}</div>
          <div className={styles.instList}>
            {feelLines.map((line, i) => (
              <div key={i} className={`${styles.instCard} ${styles.instCardFeel}`}>
                <span className={styles.instIconWrap}><IconFeel /></span>
                <span className={styles.instLine}>{line}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {avoidLines.length > 0 && (
        <div className={styles.instSection}>
          <div className={styles.instSectionLabel}>{t('rp_inst_avoid', lang)}</div>
          <div className={styles.instList}>
            {avoidLines.map((line, i) => (
              <div key={i} className={`${styles.instCard} ${styles.instCardAvoid}`}>
                <span className={styles.instIconWrap}><IconAvoid /></span>
                <span className={styles.instLine}>{line}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// ── Inline icon primitives ──
function IconPlay({ stroke = 'var(--rp-rose-400)' }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill={stroke} aria-hidden="true">
      <path d="M8 5.5v13a.6.6 0 0 0 .93.5l10-6.5a.6.6 0 0 0 0-1l-10-6.5A.6.6 0 0 0 8 5.5z" />
    </svg>
  );
}

function IconCheck({ stroke = 'var(--rp-rose-400)' }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={stroke}
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 12l5 5L20 6" />
    </svg>
  );
}

function IconRepeat({ stroke = 'currentColor' }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={stroke}
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 12a8 8 0 1 1-2.3-5.6" />
      <path d="M20 4v4.5h-4.5" />
    </svg>
  );
}

function IconBullet() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="8" />
      <path d="M8.5 12l2.5 2.5L15.5 10" />
    </svg>
  );
}

// IconFeel — heart outline, for "what you should feel" sage cards.
function IconFeel() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20s-7-4.5-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.5-7 10-7 10z" />
    </svg>
  );
}

// IconAvoid — alert triangle, for "avoid" rose cards.
function IconAvoid() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3L2 20h20L12 3z" />
      <path d="M12 10v4M12 17.5v.5" />
    </svg>
  );
}

// "Why this stretch" rationale sheet. Surfaced from a small text link
// on the IDLE screen for stretch exercises (the five poses with
// silhouette art). Renders a 2-3 sentence rationale, the supporting
// citation summary, and a DOI link out so the educational thread
// stays connected to the same /research literature shown elsewhere.
function PoseRationaleSheet({ pose, poseLabel, lang, onClose }) {
  const rationaleKey = POSE_RATIONALE_KEYS[pose];
  const citationKey = POSE_CITATIONS[pose];
  const cite = citationKey ? citation(citationKey) : null;
  const link = cite ? doiUrl(cite) : null;
  return (
    <div
      className={styles.poseRationaleOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="rp-pose-rationale-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={styles.poseRationaleSheet}>
        <h2 id="rp-pose-rationale-title" className={styles.poseRationaleTitle}>
          {poseLabel}
        </h2>
        {rationaleKey && (
          <p className={styles.poseRationaleBody}>{t(rationaleKey, lang)}</p>
        )}
        {cite && (
          <div className={styles.poseRationaleCite}>
            <div className={styles.poseRationaleCiteMeta}>
              {cite.authors} · {cite.year}
            </div>
            <p className={styles.poseRationaleCiteSummary}>
              {(cite.summary && (cite.summary[lang] || cite.summary.en)) || ''}
            </p>
            {link && (
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.poseRationaleCiteLink}
              >
                {t('rp_pose_why_research_link', lang)}
                <span aria-hidden="true" style={{ marginLeft: 4 }}>{'↗'}</span>
              </a>
            )}
          </div>
        )}
        <button
          type="button"
          className={styles.poseRationaleCloseBtn}
          onClick={onClose}
          autoFocus
        >
          {t('rp_pose_why_close_btn', lang)}
        </button>
      </div>
    </div>
  );
}
